import { useEffect } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { useApiHandlers } from 'lib/reader';
import { ContentBox } from 'components/common';
import { H5PAnswer, TaskPointsBadge } from 'components/common/tasks';
import { defaultBlockData } from 'components/editor/blocks';
import { ReaderAnswerDelete, ReaderTaskHeader } from 'components/reader/common';
import { ReaderBlockEmbedIframe } from '.';

import type {
  CommonBlockPropsReader,
  H5PUserAnswers
} from 'components/reader/blocks';

type H5PData = {
  id: number;
  completion: number;
  missingCorrectAnswers: boolean;
  points: number;
  success: number;
  score: number;
  userAnswers: Record<string, boolean> | string[];
};

type SupportedProps =
  | CommonBlockPropsReader<'embedcode'>
  | CommonBlockPropsReader<'embedlink'>
  | CommonBlockPropsReader<'h5p'>
  | CommonBlockPropsReader<'scorm'>;

const ReaderBlockEmbed = ({
  answerData,
  blockId,
  data,
  fontSize,
  onAnswerSubmitted,
  screenshotKey,
  type
}: SupportedProps) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: {
      isCompleted,
      chapterData,
      readerCourseData: { course },
      readOnly
    },
    theme
  } = useReduxState();

  const { getEmbedUrl } = useApiHandlers();

  let maxScore = 0;
  if (type === 'h5p') {
    maxScore = data.hasOwnProperty('pointsWeight')
      ? data.pointsWeight
      : defaultBlockData[type].pointsWeight;
  }

  useEffect(() => {
    window.addEventListener('message', handleReceiveMessage);
    return () => {
      window.removeEventListener('message', handleReceiveMessage);
    };
  }, []);

  const handleReceiveMessage = (ev) => {
    const blockIdMatch = (ev.data?.BlockId || ev.data?.blockId) === blockId;
    if (!blockIdMatch) return;

    const isTask = 'isTask' in data ? data?.isTask : true;
    if (!isTask) return;

    const { answerSubmitted, h5p, statusData } = ev.data;
    if (
      answerSubmitted &&
      (readOnly ||
        (isCompleted && !course?.completionAllowAnswersAfterFinishing))
    )
      return;
    else if (answerSubmitted && onAnswerSubmitted) onAnswerSubmitted(blockId);

    if (h5p && statusData && answerData?.id !== statusData.id) {
      // H5P only
      const data = JSON.parse(JSON.stringify(chapterData));

      dispatch(
        actions.updatePageState({
          chapterData: {
            ...data,
            blocks: {
              ...data.blocks,
              [blockId]: {
                ...data.blocks[blockId],
                answerData: {
                  ...data.blocks[blockId].answerData,
                  id: statusData.id,
                  completion: statusData.completion,
                  missingCorrectAnswers: statusData.missingCorrectAnswers,
                  points: Math.min(statusData.score, 1),
                  success: statusData.success,
                  score: Math.min(statusData.scoreArray.scaled, 1),
                  userAnswers: statusData.userAnswers
                } satisfies H5PData
              }
            }
          }
        })
      );
    }
  };

  const isH5P = type === 'h5p';
  const isScorm = type === 'scorm';

  return isH5P || isScorm || ('text' in data && !!data.text?.length) ? (
    <div className="embed">
      {isH5P && data.isTask && answerData?.completion ? (
        <ContentBox>
          <ReaderTaskHeader
            fontSize={fontSize}
            messages={messages}
            isMandatory={data?.isMandatory}
            title={messages.tasksEmbeddedTask}
            theme={theme}
          />

          <H5PAnswer
            answers={answerData.userAnswers || ({} as H5PUserAnswers)}
            completion={answerData.completion}
            hideScore={true}
            maxScore={maxScore}
            missingCorrectAnswers={answerData.missingCorrectAnswers}
            success={answerData.success}
            score={answerData.score}
            points={answerData.points * maxScore}
            task={{ data: data, id: blockId }}
            showPreview={true}
          />

          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <TaskPointsBadge
              hideSuccessBadge={true}
              maxPoints={maxScore}
              points={answerData.points * maxScore}
            />

            <ReaderAnswerDelete answerId={answerData.id} blockId={blockId} />
          </div>
        </ContentBox>
      ) : (
        <ReaderBlockEmbedIframe
          blockId={blockId}
          data={data}
          fontSize={fontSize}
          iframeKey={`${blockId}`}
          isScorm={isScorm}
          messages={messages}
          showHeader={(isH5P || isScorm) && data?.isMandatory}
          src={
            getEmbedUrl(blockId, type) +
            (screenshotKey ? `?key=${screenshotKey}` : '')
          }
          theme={theme}
        />
      )}

      <style jsx>{`
        .embed {
          position: relative;
        }
      `}</style>
    </div>
  ) : null;
};

export default ReaderBlockEmbed;
